import { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ProjectIcon } from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@libs/zapi-ui/ds/components/breadcrumb";

type TProjectBreadcrumbProps = {
  name: string;
  iconShape?: string | null;
  iconColor?: string | null;
  logoUrl?: string | null;
  projectsLink: ReactElement;
  onNameClick?: () => void;
  versionMenu?: ReactNode;
};

export function ProjectBreadcrumb({
  name,
  iconShape,
  iconColor,
  logoUrl,
  projectsLink,
  onNameClick,
  versionMenu
}: TProjectBreadcrumbProps) {
  const { t } = useTranslation("projectHome");
  const { t: tProjects } = useTranslation("projects");
  const nameContent = (
    <>
      <span
        data-slot="project-avatar"
        className="relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-lg ring-1 ring-foreground/10 has-data-empty:ring-0"
      >
        <ProjectIcon shape={iconShape} color={iconColor} logoUrl={logoUrl} className="size-full rounded-[inherit]" />
      </span>
      <BreadcrumbPage className="typo-label-md truncate font-medium text-muted-foreground" title={name}>
        {name}
      </BreadcrumbPage>
    </>
  );

  return (
    <Breadcrumb data-slot="project-breadcrumb" aria-label={t("header.breadcrumb")} className="min-w-0">
      <BreadcrumbList className="typo-label-md flex-nowrap gap-1.5 sm:gap-2">
        <BreadcrumbItem className="hidden md:inline-flex">
          <BreadcrumbLink
            render={projectsLink}
            className="rounded-md px-1.5 py-1 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            {tProjects("sidebar.projects")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="typo-label-md hidden text-greyscale-300 select-none md:inline-flex">
          /
        </BreadcrumbSeparator>
        <BreadcrumbItem className="min-w-0 gap-1">
          {onNameClick ? (
            <button
              type="button"
              data-testid="project-details-name-button"
              onClick={onNameClick}
              className="flex min-w-0 items-center gap-2 rounded-md px-1.5 outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              {nameContent}
            </button>
          ) : (
            <span className="flex min-w-0 items-center gap-2 px-1.5">{nameContent}</span>
          )}
          {versionMenu}
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
